/**
 * Platform Configuration
 *
 * Centralized configuration for platform-specific values like domain names.
 * Uses environment variables to support different configurations for:
 * - Local development (localhost)
 * - Staging environment
 * - Production environment
 */

// Known platform domains (used to detect if we're on a custom domain)
const KNOWN_PLATFORM_DOMAINS = ['userevstar.com', 'staging.userevstar.com'];

// Check if subdomain routing should be used in localhost
const shouldUseSubdomainInLocalhost = () => {
  // Can be disabled by setting REACT_APP_USE_SUBDOMAIN_LOCALHOST=false
  return process.env.REACT_APP_USE_SUBDOMAIN_LOCALHOST !== 'false';
};

// Check if currently deployed on Vercel
const isVercelDeployment = () => {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.includes('vercel.app');
};

// Check if hostname belongs to a known platform domain
const isKnownPlatformDomain = (hostname) => {
  return KNOWN_PLATFORM_DOMAINS.some(domain => hostname.endsWith(domain));
};

// Auto-detect the current platform domain (for merchant app)
const getPlatformDomain = () => {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';

  // For local development, use localhost
  if (env === 'development') {
    if (process.env.REACT_APP_PLATFORM_DOMAIN) return process.env.REACT_APP_PLATFORM_DOMAIN;
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const localDomain = hostname.endsWith('.localhost') ? 'localhost' : hostname;
      return `${localDomain}${window.location.port ? `:${window.location.port}` : ''}`;
    }
    return 'localhost:3000';
  }

  // Auto-detect from current URL if on Vercel
  // Return the current merchant app domain
  if (isVercelDeployment()) {
    return window.location.hostname; // e.g., revstar-merchant.vercel.app
  }

  // For production, check the current hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');

    // Check if we're on a known platform domain
    if (isKnownPlatformDomain(hostname)) {
      // Check for environment-based subdomains (e.g., staging, production, dev)
      // For URLs like dashboard.staging.userevstar.com, we want staging.userevstar.com
      const environmentKeywords = ['staging', 'production', 'prod', 'dev', 'development'];

      if (parts.length >= 3) {
        const potentialEnv = parts[parts.length - 3];
        if (environmentKeywords.includes(potentialEnv)) {
          // Return last 3 parts (e.g., staging.userevstar.com)
          return parts.slice(-3).join('.');
        }
      }

      // Extract root platform domain (e.g., dashboard.userevstar.com -> userevstar.com)
      if (parts.length >= 2) {
        return parts.slice(-2).join('.');
      }
    }

    // We're on a custom domain (not localhost, not Vercel, not platform domain)
    // Use environment variable - this is critical for custom domains to work
    console.log('[platformConfig] Custom domain detected:', hostname);
    console.log('[platformConfig] Using env var for platform domain');
  }

  // Fallback to env var or default
  return process.env.REACT_APP_PLATFORM_DOMAIN || process.env.REACT_APP_PLATFORM_DOMAIN_PRODUCTION || 'userevstar.com';
};

// Get the full store URL for a given slug
export const getStoreUrl = (slug) => {
  const domain = getPlatformDomain();
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';

  // For local development
  if (env === 'development') {
    // Use subdomain routing in localhost if enabled (modern browsers support *.localhost)
    if (shouldUseSubdomainInLocalhost()) {
      return `http://${slug}.${domain}`;
    }
    // Fallback to path-based routing
    return `http://${domain}/s/${slug}`;
  }

  // For Vercel deployments: use path-based routing (Vercel doesn't support wildcard subdomains)
  if (isVercelDeployment()) {
    return `https://${domain}/s/${slug}`;
  }

  // For custom domains: use subdomain routing
  return `https://${slug}.${domain}`;
};

// Get the platform domain
export const getPlatformDomainName = () => {
  return getPlatformDomain();
};

// Get the store subdomain URL (for display purposes)
export const getStoreSubdomain = (slug) => {
  const domain = getPlatformDomain();
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';

  // For local development
  if (env === 'development') {
    // Use subdomain format if enabled
    if (shouldUseSubdomainInLocalhost()) {
      return `${slug}.${domain}`;
    }
    // Fallback to path-based format
    return `${domain}/s/${slug}`;
  }

  // For Vercel deployments: show path-based format
  if (isVercelDeployment()) {
    return `${domain}/s/${slug}`;
  }

  // For custom domains: show subdomain format
  return `${slug}.${domain}`;
};

// Get the public marketplace URL
export const getMarketplaceUrl = () => {
  const domain = getPlatformDomain();
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';

  if (env === 'development') {
    if (shouldUseSubdomainInLocalhost()) {
      return `http://marketplace.${domain}`;
    }
    return `http://${domain}/marketplace`;
  }

  if (isVercelDeployment()) {
    return `https://marketplace.${domain}`;
  }

  return `https://marketplace.${domain}`;
};

// Check if running in production environment
export const isProduction = () => {
  const env = process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development';
  return env === 'production';
};

// Export default config object
const platformConfig = {
  domain: getPlatformDomain(),
  getStoreUrl,
  getStoreSubdomain,
  getMarketplaceUrl,
  getPlatformDomainName,
  isProduction,
  environment: process.env.REACT_APP_ENV || process.env.NODE_ENV || 'development'
};

export default platformConfig;
